import React, { useEffect, useState } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { useHistory } from 'react-router-dom';
import PropTypes from 'prop-types';
import moment from 'moment-timezone';

import EventDeskTabLayout from '../EventDeskTabLayout';
import DrawList from './DrawList';
import CourtDeskConfig from './CourtDeskConfig';
import CourtGridHeader from './CourtGridHeader';
import CourtGridCalendar from './CourtGridCalendar';

import { getActiveFacilitiesAndCourts } from '../../../utils/courtSchedular';
import { useStatsig } from '../../../components/hooks/useStatsig';

import { EventDeskTabKeys } from '../../../utils/eventDeskConstants';

import {
    clearCourtSchedularData,
    closeEditDrawer,
    getCourtConfigData,
    updateDrawsListForCourtScheduler,
    updateSaveConfigViewDisplay,
    updateScheduledMatchSettings,
} from '../../../actions/court-scheduler-actions';

import styles from './courtSchedular.scss';
import DrawEditConfig from './DrawEditConfig';
import EditMatchSettingsConfig from './EditMatchSettingsConfig';

import * as drawEditActions from '../../../actions/draw-edit-actions';

const CourtSchedular = (props) => {
    const {
        setEventDeskActiveView,
    } = props;

    const history = useHistory();
    const dispatch = useDispatch();
    const { checkGate, featureGates } = useStatsig();
    const isScheduleEnabled = checkGate(featureGates.EVENT_DESK_SCHEDULE);

    const {
        event,
        players,
        teams,
        draws,
        locations,
        officials,
        drawDivision,
    } = useSelector(({ drawEdit }) => {
        const { event, draws, players, teams, locations, officials, drawDivision } = drawEdit;
        return {
            event: event?.data,
            players,
            teams,
            draws,
            locations: locations?.data,
            officials: officials?.data,
            drawDivision,
        };
    });

    const courtScheduler = useSelector(({ courtScheduler }) => courtScheduler);

    useEffect(() => {
        if (event && draws.data?.length > 0) {
            const { eventSchedule } = event;
            dispatch(updateDrawsListForCourtScheduler(draws, eventSchedule, event.id));
        }
    }, [draws, event]);

    useEffect(() => {
        return () => {
            // Clear court schedular data so everything is fetched the next time
            dispatch(clearCourtSchedularData());
        };
    }, []);

    const [drawToEditSettings, setDrawToEditSettings] = useState();
    const [editMatch, setEditMatch] = useState(null);

    const activeFacilitiesAndCourts = getActiveFacilitiesAndCourts(locations || []);

    useEffect(() => {
        if (event && !isScheduleEnabled) {
            // load first draw
            dispatch(drawEditActions.getDraw({ eventId: event.id, drawId: draws.data?.[0]?.id, setAsCurrentDraw: true }));
            history.replace({
                search: `?v=${EventDeskTabKeys.DRAW_EDITOR}&d=${draws.data?.[0]?.id}`,
            });
            setEventDeskActiveView(EventDeskTabKeys.DRAW_EDITOR);
        }
    }, []);

    useEffect(() => {
        if (!event || locations?.length <= 0) {
            return;
        }
        // If the event has draws, wait for drawsList to be populated before
        // fetching config/scheduled matches. Scheduled matches are transformed
        // using drawsList; running the transform too early produces match
        // objects without draw details.
        const hasDraws = draws.data?.length > 0;
        if (hasDraws && courtScheduler.drawsList.length === 0) {
            return;
        }

        const { id, eventSchedule } = event;

        const currentDate = moment().tz(eventSchedule.timeZone?.value).startOf('day');
        const eventStartDate = moment.utc(eventSchedule.eventStartUtc).tz(eventSchedule.timeZone?.value).startOf('day');
        const eventEndDate = moment.utc(eventSchedule.eventEndUtc).tz(eventSchedule.timeZone?.value).startOf('day');

        const initialDate = currentDate.isBefore(eventStartDate) || currentDate.isAfter(eventEndDate)
                ? eventStartDate
                : currentDate;

        dispatch(getCourtConfigData(id, initialDate.toISOString()));
    }, [event, locations, courtScheduler.drawsList.length, draws.data?.length]);

    const onChangeDate = (date) => {
        const { eventSchedule } = event;

        const selectedDate = moment(date).tz(eventSchedule.timeZone?.value).startOf('day').toISOString();
        dispatch(getCourtConfigData(event.id, selectedDate));
    };

    const onEditConfig = () => {
        dispatch(updateSaveConfigViewDisplay(true));
    };

    const closeEditCourtDeskConfig = () => {
        dispatch(updateSaveConfigViewDisplay(false));
    };

    const onDrawSettingsEdit = (draw) => {
        setDrawToEditSettings(draw);
    };

    const closeDrawSettingsEdit = () => {
        setDrawToEditSettings(null);
    };

    const onEditMatchSettings = (match) => {
        setEditMatch(match);
    };

    const closeEditMatchSettings = () => {
        setEditMatch(null);
        dispatch(closeEditDrawer());
    };

    const handlePostSubmit = (eventId, matchId, match, requestBody) => {
        dispatch(updateScheduledMatchSettings(eventId, matchId, match, requestBody));
    };

    useEffect(() => {
      if (courtScheduler?.hasSavedSuccess) {
        setEditMatch(null);
      }
    }, [courtScheduler?.hasSavedSuccess]);

    const shouldDisplayCalendar = courtScheduler.selectedDate && courtScheduler.selectedDateCourtConfig;

    return (
        <EventDeskTabLayout
            noPadding
            fitScreen={true}
        >
            <div className={`${styles.courtSchedular} d-flex`}>
                <DrawList
                    className={styles.sidebar}
                    draws={courtScheduler.drawsList}
                    event={event}
                    onDrawSettingsEdit={onDrawSettingsEdit}
                />
                <div className={`${styles.courtGrid} flex-1 mw0`}>
                    {courtScheduler.selectedDate && (
                        <CourtGridHeader
                            onClickSettings={onEditConfig}
                            onChangeDate={onChangeDate}
                            event={event}
                            history={history}
                        />
                    )}
                    {shouldDisplayCalendar && (
                        <CourtGridCalendar
                            event={event}
                            onEditMatchSettings={onEditMatchSettings}
                        />
                    )}
                </div>
            </div>
            {courtScheduler.showConfigModal && (
                <CourtDeskConfig
                    eventId={event?.id}
                    eventSchedule={event?.eventSchedule}
                    isEdit={!!courtScheduler.selectedDateCourtConfig}
                    closeEditCourtDeskConfig={closeEditCourtDeskConfig}
                    activeFacilitiesAndCourts={activeFacilitiesAndCourts}
                />
            )}
            {drawToEditSettings && (
                <DrawEditConfig
                    eventId={event?.id}
                    eventSchedule={event?.eventSchedule}
                    draw={drawToEditSettings}
                    closeSideBar={closeDrawSettingsEdit}
                    sportTypeId={event?.sportTypeId}
                    eventTypeId={event?.eventTypeId}
                    eventAllottedMatchTimes={event?.matchTimes}
                    drawDivision={drawDivision}
                    allowMomentTimeZoneOverride={!shouldDisplayCalendar}
                />
            )}
            {editMatch && (
                <EditMatchSettingsConfig
                    eventId={event?.id}
                    sportTypeId={event?.sportTypeId}
                    utrTypeId={event?.utrTypeId}
                    eventSchedule={event?.eventSchedule}
                    className={styles.matchSettings}
                    match={editMatch}
                    officials={officials}
                    playersDictByMemberId={players?.playersDictByMemberId}
                    teamsDict={teams?.teamsDict}
                    closeSideBar={closeEditMatchSettings}
                    onPostSubmit={handlePostSubmit}
                    isSubmitting={courtScheduler?.isSaving}
                    savingError={courtScheduler?.savingError}
                    clubTierType={event.club?.clubTierType || event.club?.tierType || event.club?.tierTypeId}
                />
            )}
        </EventDeskTabLayout>
    );
};

CourtSchedular.propTypes = {
    setEventDeskActiveView: PropTypes.func.isRequired,
};

CourtSchedular.defaultProps = {};

export default CourtSchedular;
